import type { FC } from "react";
import { HighlightedMarkdown } from "~/components/HighlightedMarkdown";
import { HighlightedMdastMarkdown } from "~/components/HighlightedMdastMarkdown";
import {
  PREVIEW_RELEASE_MODE,
  useReleaseMode,
} from "~/features/releaseMode/useReleaseMode";
import type { TermMatcher } from "~/utils/termMatcher";
import type { Hint } from "../../types";
import { getSectionMdast, reportToHastHandlers } from "./mdast/mdastToHast";
import { rehypeGroupSourceCitations } from "./rehypeGroupSourceCitations";
import { reportCitationComponents } from "./ReportSourceCitationGroup";

const CITATION_GROUP_PLUGINS = [rehypeGroupSourceCitations];

type ReportContentProps = {
  content: string;
  contentData?: unknown;
  terms: Hint[];
  medicationMatcher?: TermMatcher | null;
  className?: string;
};

export const StableReportContent: FC<ReportContentProps> = ({
  content,
  terms,
  medicationMatcher,
  className,
}) => (
  <HighlightedMarkdown
    className={className}
    terms={terms}
    medicationMatcher={medicationMatcher}
    extraRehypePluginsAfterRaw={CITATION_GROUP_PLUGINS}
    components={reportCitationComponents}
  >
    {content}
  </HighlightedMarkdown>
);

export const ReleaseModeReportContent: FC<ReportContentProps> = ({
  content,
  contentData,
  terms,
  medicationMatcher,
  className,
}) => {
  const releaseMode = useReleaseMode();
  const tree = getSectionMdast(contentData);

  if (releaseMode !== PREVIEW_RELEASE_MODE || !tree) {
    return (
      <StableReportContent
        content={content}
        terms={terms}
        medicationMatcher={medicationMatcher}
        className={className}
      />
    );
  }

  return (
    <HighlightedMdastMarkdown
      tree={tree}
      terms={terms}
      medicationMatcher={medicationMatcher}
      toHastHandlers={reportToHastHandlers}
      extraRehypePluginsAfterRaw={CITATION_GROUP_PLUGINS}
      components={reportCitationComponents}
      className={className}
    />
  );
};
